import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import StarRating from '@/components/ui/star-rating';
import { useToast } from '@/hooks/use-toast';
import { Heart, MessageSquare, Plus } from 'lucide-react';
import { format } from 'date-fns';

interface Review {
  id: string;
  customer_name: string;
  rating: number;
  review_text: string;
  likes_count: number;
  created_at: string;
  user_liked?: boolean;
}

interface ProductReviewsProps {
  productId: string;
  productName: string;
}

const ProductReviews = ({ productId, productName }: ProductReviewsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [canReview, setCanReview] = useState(false);

  // Check if user can review this product (has purchased and delivered)
  useEffect(() => {
    const checkCanReview = async () => {
      if (!user) {
        setCanReview(false);
        return;
      }

      try {
        // Check if user has purchased this product and it's delivered
        const { data: orders } = await supabase
          .from('orders')
          .select(`
            id,
            status,
            order_items!inner(product_id)
          `)
          .eq('user_id', user.id)
          .eq('order_items.product_id', productId)
          .eq('status', 'delivered');

        // Check if user already reviewed this product
        const { data: existingReview } = await supabase
          .from('product_reviews')
          .select('id')
          .eq('user_id', user.id)
          .eq('product_id', productId)
          .single();

        setCanReview(orders && orders.length > 0 && !existingReview);
      } catch (error) {
        setCanReview(false);
      }
    };

    checkCanReview();
  }, [user, productId]);

  // Fetch reviews for this product
  const { data: reviews = [] } = useQuery({
    queryKey: ['product-reviews', productId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_reviews')
        .select('*')
        .eq('product_id', productId)
        .order('likes_count', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Check which reviews the current user has liked
      if (user && data.length > 0) {
        const { data: userLikes } = await supabase
          .from('review_likes')
          .select('review_id')
          .eq('user_id', user.id)
          .in('review_id', data.map(r => r.id));

        const likedReviewIds = new Set(userLikes?.map(l => l.review_id) || []);
        
        return data.map(review => ({
          ...review,
          user_liked: likedReviewIds.has(review.id)
        }));
      }

      return data;
    },
  });

  // Submit review mutation
  const submitReview = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Must be logged in');

      // Get user profile for name
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', user.id)
        .single();

      const { error } = await supabase
        .from('product_reviews')
        .insert({
          product_id: productId,
          user_id: user.id,
          customer_name: profile?.full_name || 'Anonymous',
          rating,
          review_text: reviewText
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Review submitted successfully!' });
      setIsDialogOpen(false);
      setRating(5);
      setReviewText('');
      queryClient.invalidateQueries({ queryKey: ['product-reviews', productId] });
      queryClient.invalidateQueries({ queryKey: ['top-reviews'] });
    },
    onError: (error) => {
      toast({
        title: 'Error submitting review',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Like/unlike review mutation
  const toggleLike = useMutation({
    mutationFn: async ({ reviewId, isLiked }: { reviewId: string; isLiked: boolean }) => {
      if (!user) throw new Error('Must be logged in');

      if (isLiked) {
        // Remove like
        const { error } = await supabase
          .from('review_likes')
          .delete()
          .eq('review_id', reviewId)
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        // Add like
        const { error } = await supabase
          .from('review_likes')
          .insert({
            review_id: reviewId,
            user_id: user.id
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-reviews', productId] });
      queryClient.invalidateQueries({ queryKey: ['top-reviews'] });
    },
    onError: (error) => {
      toast({
        title: 'Error updating like',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const handleSubmitReview = () => {
    if (!reviewText.trim()) {
      toast({
        title: 'Please write a review',
        variant: 'destructive'
      });
      return;
    }
    submitReview.mutate();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Customer Reviews ({reviews.length})
        </h3>
        
        {canReview && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Write Review
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Write a Review</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Rating</Label>
                  <div className="mt-2">
                    <StarRating
                      rating={rating}
                      interactive
                      onRatingChange={setRating}
                      size="lg"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="review">Your Review</Label>
                  <Textarea
                    id="review"
                    placeholder="Share your experience with this product..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    rows={4}
                    className="mt-2"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSubmitReview}
                    disabled={submitReview.isPending}
                    className="flex-1"
                  >
                    {submitReview.isPending ? 'Submitting...' : 'Submit Review'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {reviews.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No reviews yet. Be the first to review this product!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <StarRating rating={review.rating} size="sm" />
                        <span className="font-medium text-sm">{review.customer_name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(review.created_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    
                    {user && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleLike.mutate({
                          reviewId: review.id,
                          isLiked: review.user_liked || false
                        })}
                        disabled={toggleLike.isPending}
                        className="gap-1 h-8"
                      >
                        <Heart
                          className={`h-3 w-3 ${
                            review.user_liked
                              ? 'fill-red-500 text-red-500'
                              : 'text-muted-foreground'
                          }`}
                        />
                        <span className="text-xs">{review.likes_count}</span>
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-sm text-foreground">{review.review_text}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;